import { useCallback, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { updateDialogAlerts } from '@/redux/dialogAlerts/actions';
import { selectNodes } from '@/redux/nodes/selectors';
import { selectBrainId } from '@/redux/session/selectors';
import { Action } from '@/shared/types/action';
import { DialogActionType } from '@/shared/types/dialog';
import { Node } from '@/shared/types/node';
import { WHATSAPP_BUTTON_TEXT_LIMIT } from '@/util/validation/constants';
import { getWhatsappButtonLabelLength } from '@/util/validation/schemas/whatsapp-schemas';

import useDialogs from './useDialogs';
import useWhatsAppActive from './useWhatsAppActive';

const MAX_WPP_QUICKOPTIONS = 10;

const isOverWhatsappButtonLimit = (label: string | undefined) =>
  getWhatsappButtonLabelLength(label ?? '') > WHATSAPP_BUTTON_TEXT_LIMIT;

const useWhatsAppWarnings = () => {
  const dispatch = useDispatch();
  const brainId = useSelector(selectBrainId);
  const { dialogs } = useDialogs(brainId);
  const whatsAppImplemented = useWhatsAppActive();

  const computeDialogsWarnings = useCallback(
    (actions: Action[], node: Node, type: string, parentId: string, dialogId: string) => {
      actions?.forEach((action, index) => {
        // Skip action types that can't produce a WhatsApp warning — otherwise
        // every url/image/audio/handover/etc. in every saved dialog would
        // trigger a pointless Redux dispatch on load. Safe because action
        // types can't change in place in this codebase: a type swap is
        // implemented as delete+re-create with a new action_id, so no stale
        // warning can be keyed to an id that's still in the tree.
        if (action?.type !== 'text' && action?.type !== 'carousel' && action?.type !== 'webview') {
          return;
        }

        const baseAlert = {
          id: type === 'requisite' ? parentId : action.action_id,
          nodeId: node.node_id,
          index,
          dialogId,
        };

        if (action.type === 'text' && action?.options?.length > MAX_WPP_QUICKOPTIONS) {
          dispatch(
            updateDialogAlerts({
              ...baseAlert,
              type: type === 'requisite' ? 'question' : action.type,
              body: 'dialog.warnings.whatsapp.quickoptions',
              title:
                type === 'requisite'
                  ? 'dialog.warnings.question_title'
                  : 'dialog.warnings.text_title',
              alertType: 'warning',
            })
          );
        } else if (
          action.type === 'carousel' &&
          action.cards?.some((card) =>
            card.buttons?.some((b) => isOverWhatsappButtonLimit(b.label))
          )
        ) {
          dispatch(
            updateDialogAlerts({
              ...baseAlert,
              type: action.type,
              body: 'dialog.warnings.whatsapp.button_label_too_long',
              title: 'dialog.warnings.carousel_title',
              alertType: 'warning',
            })
          );
        } else if (action.type === 'webview' && isOverWhatsappButtonLimit(action.label)) {
          dispatch(
            updateDialogAlerts({
              ...baseAlert,
              type: action.type,
              body: 'dialog.warnings.whatsapp.button_label_too_long',
              title: 'dialog.warnings.webview_title',
              alertType: 'warning',
            })
          );
        } else {
          dispatch(
            updateDialogAlerts({
              ...baseAlert,
              type: type === 'requisite' ? 'question' : (action.type as DialogActionType),
              body: '',
              title: `dialog.warnings.${type === 'requisite' ? 'question' : 'text'}_title`,
              alertType: 'warning',
            })
          );
        }
      });
    },
    [dispatch]
  );

  const evaluateNode = useCallback(
    (nodes: Node[], dialogId: string) => {
      nodes?.forEach((node) => {
        node?.requisites?.forEach((requisite) => {
          computeDialogsWarnings(
            requisite?.actions,
            node,
            'requisite',
            requisite.requisite_id,
            dialogId
          );
        });
        node?.conditions?.forEach((condition) => {
          computeDialogsWarnings(
            condition?.actions,
            node,
            'condition',
            condition.condition_id,
            dialogId
          );
        });
        computeDialogsWarnings(node?.actions, node, 'action', node.node_id, dialogId);
      });
    },
    [computeDialogsWarnings]
  );

  const nodes = useSelector(selectNodes);

  useEffect(() => {
    if (whatsAppImplemented) {
      evaluateNode(nodes.nodes, nodes.dialogId);

      const savedDialogs = dialogs?.filter((dialog) => dialog.dialog_id !== nodes.dialogId);
      savedDialogs?.forEach((dialog) => {
        evaluateNode(dialog.nodes, dialog.dialog_id);
      });
    }
  }, [dialogs, whatsAppImplemented, evaluateNode, nodes.dialogId, nodes.nodes]);
};

export default useWhatsAppWarnings;
